import React, { useState, useCallback } from 'react';
import { View, Text, StyleSheet, ScrollView, KeyboardAvoidingView, Platform, TouchableOpacity, TextInput, Linking, Switch } from 'react-native';
import { useRouter, useFocusEffect } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { useAuth } from '../../src/context/AuthContext';
import { useToast } from '../../src/components/Toast';
import { useTheme } from '../../src/context/ThemeContext';
import type { ColorTheme } from '../../src/utils/theme';
import { api, saveToken } from '../../src/api/client';
import { SMS_UI_ENABLED } from '../../src/config/features';
import Button from '../../src/components/Button';
import InlineConfirm from '../../src/components/InlineConfirm';
import Input from '../../src/components/Input';
import Card from '../../src/components/Card';
import { spacing, fontSize, fontWeight, borderRadius } from '../../src/utils/theme';
import { rs } from '../../src/utils/responsive';
import ContentContainer from '../../src/components/ContentContainer';
import { getTimezone, setTimezone, TIMEZONE_OPTIONS } from '../../src/utils/timezone';
import Picker from '../../src/components/Picker';
import { titleCase, formatPhone } from '../../src/utils/format';

interface Profile {
  readonly id: string;
  readonly accountNumber: string;
  readonly email: string;
  readonly name?: string;
  readonly phone?: string;
}

/** UserAccountScreen - Profile editing, password change, 2FA, theme, timezone, and feedback. */
export default function UserAccountScreen() {
  const { user, logout } = useAuth();
  const router = useRouter();
  const toast = useToast();
  const { colors, themeMode, setThemeMode } = useTheme();
  const styles = useStyles(colors);

  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  // Edit states
  const [editingProfile, setEditingProfile] = useState(false);
  const [editEmail, setEditEmail] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editName, setEditName] = useState('');
  const [saving, setSaving] = useState(false);

  // Password states
  const [changingPassword, setChangingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);
  const [selectedTz, setSelectedTz] = useState(getTimezone());
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [savingSms, setSavingSms] = useState(false);

  /** PUT /api/auth/profile with just the SMS consent flag. */
  const handleToggleSms = async (next: boolean) => {
    setSavingSms(true);
    setSmsOptIn(next);
    try {
      await api('/api/auth/profile', { method: 'PUT', body: { smsMarketingOptIn: next } });
      toast.show({ type: 'success', title: next ? 'Text offers on' : 'Text offers off' });
    } catch (err: any) {
      setSmsOptIn(!next);
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally { setSavingSms(false); }
  };

  /** GET /api/auth/profile — load user profile and 2FA status. */
  const fetchProfile = async () => {
    try {
      const data = await api<Profile & { twoFactorEnabled?: boolean; smsMarketingOptIn?: boolean }>('/api/auth/profile');
      setProfile(data);
      setEditEmail(data.email);
      setEditPhone(data.phone || '');
      setEditName(data.name || '');
      setTwoFactorEnabled(!!data.twoFactorEnabled);
      setSmsOptIn(!!data.smsMarketingOptIn);
    } catch (err) { console.error('fetch profile:', err); } finally { setLoading(false); }
  };

  useFocusEffect(useCallback(() => { fetchProfile(); }, []));

  /** PUT /api/auth/profile — save changed profile fields. */
  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const body: any = {};
      if (editEmail !== profile?.email) body.email = editEmail;
      if (editPhone !== (profile?.phone || '')) body.phone = editPhone;
      if (editName !== (profile?.name || '')) body.name = editName;

      if (Object.keys(body).length === 0) {
        setEditingProfile(false);
        return;
      }

      const res = await api<{ success: boolean; token: string; profile: Profile }>('/api/auth/profile', {
        method: 'PUT',
        body,
      });
      if (res.token) await saveToken(res.token);
      setProfile(res.profile);
      setEditingProfile(false);
      toast.show({ type: 'success', title: 'Profile updated' });
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally { setSaving(false); }
  };

  const MIN_PASSWORD_LENGTH = 8;

  /** POST /api/auth/change-password — update password with current password verification. */
  const handleChangePassword = async () => {
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      toast.show({ type: 'error', title: 'Password must be at least 8 characters' }); return;
    }
    if (!/[A-Z]/.test(newPassword) || !/[a-z]/.test(newPassword) || !/[0-9]/.test(newPassword) || !/[^A-Za-z0-9]/.test(newPassword)) {
      toast.show({ type: 'error', title: 'Password must include upper, lower, number, and special character' }); return;
    }
    if (newPassword !== confirmPassword) {
      toast.show({ type: 'error', title: 'Passwords do not match' }); return;
      return;
    }
    setSavingPassword(true);
    try {
      await api('/api/auth/change-password', {
        method: 'POST',
        body: { currentPassword, newPassword },
      });
      setChangingPassword(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast.show({ type: 'success', title: 'Password changed successfully' });
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally { setSavingPassword(false); }
  };

  // 2FA state
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [toggling2FA, setToggling2FA] = useState(false);
  const [show2FACode, setShow2FACode] = useState(false);
  const [tfaCode, setTfaCode] = useState('');

  // Feedback state
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackType, setFeedbackType] = useState('praise');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [sendingFeedback, setSendingFeedback] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);

  // Delete account state
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  /** POST /api/feedback — submit user feedback with type, message, and optional rating. */
  const handleSubmitFeedback = async () => {
    if (!feedbackMessage.trim()) {
      toast.show({ type: 'error', title: 'Please enter a message' }); return;
    }
    setSendingFeedback(true);
    try {
      await api('/api/feedback', {
        method: 'POST',
        body: { type: feedbackType, message: feedbackMessage, rating: feedbackRating || undefined, screen: 'account' },
      });
      setShowFeedback(false);
      setFeedbackMessage('');
      setFeedbackRating(0);
      toast.show({ type: 'success', title: 'Feedback sent!', message: 'Thank you for your feedback.' });
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally { setSendingFeedback(false); }
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) { setDeleteError('Please enter your password'); return; }
    setDeleteError('');
    setDeletingAccount(true);
    try {
      await api('/api/privacy/account?confirm=yes', { method: 'DELETE', body: { password: deletePassword } });
      toast.show({ type: 'success', title: 'Account deleted' });
      logout();
    } catch (err: any) {
      setDeleteError(err.message || 'Failed to delete account');
    } finally { setDeletingAccount(false); }
  };

  /** POST /api/auth/toggle-2fa — enable/disable two-factor auth (email-based). */
  const handleToggle2FA = async () => {
    if (twoFactorEnabled) {
      // Disabling — no code needed
      setToggling2FA(true);
      try {
        await api('/api/auth/toggle-2fa', { method: 'POST', body: { enable: false } });
        setTwoFactorEnabled(false);
        toast.show({ type: 'success', title: '2FA disabled' });
      } catch (err: any) { toast.show({ type: 'error', title: 'Error', message: err.message }); }
      finally { setToggling2FA(false); }
    } else if (!show2FACode) {
      // Enabling — request code first
      setToggling2FA(true);
      try {
        await api('/api/auth/toggle-2fa', { method: 'POST', body: { enable: true } });
        setShow2FACode(true);
        toast.show({ type: 'info', title: 'Code sent to your email' });
      } catch (err: any) { toast.show({ type: 'error', title: 'Error', message: err.message }); }
      finally { setToggling2FA(false); }
    } else {
      // Enabling — verify code
      if (tfaCode.length !== 6) { toast.show({ type: 'error', title: 'Enter 6-digit code' }); return; }
      setToggling2FA(true);
      try {
        await api('/api/auth/toggle-2fa', { method: 'POST', body: { enable: true, code: tfaCode } });
        setTwoFactorEnabled(true);
        setShow2FACode(false);
        setTfaCode('');
        toast.show({ type: 'success', title: '2FA enabled' });
      } catch (err: any) { toast.show({ type: 'error', title: 'Error', message: err.message }); }
      finally { setToggling2FA(false); }
    }
  };

  const handleLogout = () => {
    setConfirmLogout(true);
  };

  return (
    <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
      <ScrollView style={styles.container} contentContainerStyle={styles.content}>
        <ContentContainer maxWidth={720}>
        {/* Profile Card */}
        <Card style={styles.profileCard}>
          {profile?.accountNumber && (
            <View style={styles.accountRow}>
              <Text style={styles.accountLabel}>Account:</Text>
              <Text style={styles.accountNumber}>{profile.accountNumber}</Text>
            </View>
          )}
          <View style={styles.avatar}>
            <Ionicons name="person" size={32} color={colors.primary} />
          </View>
          <Text style={styles.name}>{titleCase(profile?.name || user?.name) || 'Customer'}</Text>
          <Text style={styles.email}>{profile?.email || user?.email}</Text>
          {profile?.phone ? <Text style={styles.phone}>{formatPhone(profile.phone)}</Text> : null}
        </Card>

        {/* Edit Profile */}
        {!editingProfile ? (
          <Button
            title="Edit Profile"
            onPress={() => setEditingProfile(true)}
            variant="outline"
            icon={<Ionicons name="create-outline" size={20} color={colors.primary} />}
            style={styles.actionBtn}
          />
        ) : (
          <Card style={styles.editCard}>
            <Text style={styles.sectionTitle}>Edit Profile</Text>
            <Input label="Name" icon="person-outline" value={editName} onChangeText={setEditName} />
            <Input label="Email" icon="mail-outline" value={editEmail} onChangeText={setEditEmail} keyboardType="email-address" autoCapitalize="none" maxLength={255} />
            <Input label="Phone" icon="call-outline" value={editPhone} onChangeText={setEditPhone} keyboardType="phone-pad" />
            <View style={styles.btnRow}>
              <Button title="Cancel" onPress={() => setEditingProfile(false)} variant="ghost" style={{ flex: 1 }} />
              <Button title="Save" onPress={handleSaveProfile} loading={saving} style={{ flex: 1 }} />
            </View>
          </Card>
        )}

        {/* Change Password */}
        {!changingPassword ? (
          <Button
            title="Change Password"
            onPress={() => setChangingPassword(true)}
            variant="outline"
            icon={<Ionicons name="lock-closed-outline" size={20} color={colors.primary} />}
            style={styles.actionBtn}
          />
        ) : (
          <Card style={styles.editCard}>
            <Text style={styles.sectionTitle}>Change Password</Text>
            <Input label="Current Password" icon="lock-closed-outline" value={currentPassword} onChangeText={setCurrentPassword} secureTextEntry autoCapitalize="none" autoCorrect={false} />
            <Input label="New Password" icon="lock-open-outline" value={newPassword} onChangeText={setNewPassword} secureTextEntry autoCapitalize="none" autoCorrect={false} placeholder="Min. 8 characters" />
            <Input label="Confirm New Password" icon="lock-open-outline" value={confirmPassword} onChangeText={setConfirmPassword} secureTextEntry autoCapitalize="none" autoCorrect={false} />
            <View style={styles.btnRow}>
              <Button title="Cancel" onPress={() => { setChangingPassword(false); setCurrentPassword(''); setNewPassword(''); setConfirmPassword(''); }} variant="ghost" style={{ flex: 1 }} />
              <Button title="Update" onPress={handleChangePassword} loading={savingPassword} style={{ flex: 1 }} />
            </View>
          </Card>
        )}

        {/* Payment Methods */}
        <Card style={styles.section}>
          <Text style={styles.sectionTitle}>Payment Methods</Text>
          <Button
            title="Manage Cards"
            onPress={() => router.push('/payment-methods')}
            variant="outline"
            icon={<Ionicons name="card" size={20} color={colors.primary} />}
            style={{ marginTop: spacing.sm }}
          />
        </Card>

        {/* Text message offers (hidden until SMS is activated) */}
        {SMS_UI_ENABLED && (
        <Card style={styles.section}>
          <Text style={styles.sectionTitle}>Text Message Offers</Text>
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
            <Text style={{ flex: 1, fontSize: fontSize.xs, color: colors.textSecondary, paddingRight: spacing.sm }}>
              Get offers and updates from your car washes by text{profile?.phone ? '' : ' (add a phone number first)'}. Msg & data rates may apply. Reply STOP anytime.
            </Text>
            <Switch
              value={smsOptIn}
              disabled={!profile?.phone || savingSms}
              onValueChange={handleToggleSms}
              trackColor={{ false: colors.borderLight, true: colors.primary + '80' }}
              thumbColor={smsOptIn ? colors.primary : colors.textLight}
            />
          </View>
        </Card>
        )}

        {/* Timezone */}
        <Card style={styles.section}>
          <Text style={styles.sectionTitle}>Timezone</Text>
          <Text style={{ fontSize: fontSize.xs, color: colors.textSecondary, marginTop: -spacing.sm, marginBottom: spacing.sm }}>
            Used for displaying transaction dates and times.
          </Text>
          <Picker
            options={TIMEZONE_OPTIONS}
            value={selectedTz}
            onValueChange={(val) => {
              setSelectedTz(val);
              setTimezone(val);
              toast.show({ type: 'success', title: `Timezone updated` });
            }}
          />
        </Card>

        {/* Appearance */}
        <Card style={styles.section}>
          <Text style={styles.sectionTitle}>Appearance</Text>
          <View style={{ flexDirection: 'row', gap: spacing.sm }}>
            {(['light', 'dark', 'system'] as const).map(mode => (
              <TouchableOpacity
                key={mode}
                onPress={() => setThemeMode(mode)}
                style={[styles.fbChip, themeMode === mode && styles.fbChipActive]}
              >
                <Text style={[styles.fbChipText, themeMode === mode && { color: colors.white }]}>
                  {mode.charAt(0).toUpperCase() + mode.slice(1)}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </Card>

        {/* Security — 2FA */}
        <Card style={styles.section}>
          <Text style={styles.sectionTitle}>Security</Text>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
            <View style={{ flex: 1 }}>
              <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.text }}>Two-Factor Authentication</Text>
              <Text style={{ fontSize: fontSize.xs, color: colors.textSecondary, marginTop: 2 }}>
                {twoFactorEnabled ? 'Enabled — login code sent via email' : 'Add extra security to your account'}
              </Text>
            </View>
            <Button
              title={twoFactorEnabled ? 'Disable' : 'Enable'}
              onPress={handleToggle2FA}
              loading={toggling2FA}
              variant={twoFactorEnabled ? 'danger' : 'outline'}
              size="sm"
            />
          </View>
          {show2FACode && (
            <View style={{ marginTop: spacing.sm }}>
              <Input label="Enter 6-digit code from email" icon="key-outline" value={tfaCode} onChangeText={setTfaCode} keyboardType="number-pad" maxLength={6} />
              <Button title="Confirm" onPress={handleToggle2FA} loading={toggling2FA} style={{ marginTop: spacing.xs }} />
            </View>
          )}
        </Card>

        {/* Terms of Service */}
        <Button
          title="Terms of Service"
          onPress={() => router.push('/terms')}
          variant="ghost"
          icon={<Ionicons name="document-text-outline" size={20} color={colors.textSecondary} />}
          style={[styles.actionBtn, { borderWidth: 1, borderColor: colors.primary + '40', borderRadius: borderRadius.md }]}
        />

        {/* Support */}
        <Button
          title="support@rinsepay.app"
          onPress={() => Linking.openURL('mailto:support@rinsepay.app')}
          variant="ghost"
          icon={<Ionicons name="mail-outline" size={20} color={colors.textSecondary} />}
          style={[styles.actionBtn, { borderWidth: 1, borderColor: colors.primary + '40', borderRadius: borderRadius.md }]}
        />

        {/* Feedback */}
        {!showFeedback ? (
          <Button
            title="Send Feedback"
            onPress={() => setShowFeedback(true)}
            variant="outline"
            icon={<Ionicons name="chatbubble-ellipses-outline" size={20} color={colors.primary} />}
            style={styles.actionBtn}
          />
        ) : (
          <Card style={styles.editCard}>
            <Text style={styles.sectionTitle}>Send Feedback</Text>
            <Text style={{ fontSize: fontSize.xs, color: colors.textSecondary, marginTop: -spacing.sm, marginBottom: spacing.sm }}>
              Help us improve! Let us know what you think.
            </Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs, marginBottom: spacing.sm }}>
              {['praise', 'feature', 'bug', 'complaint'].map(t => (
                <TouchableOpacity key={t} onPress={() => setFeedbackType(t)}
                  style={[styles.fbChip, feedbackType === t && styles.fbChipActive]}>
                  <Text style={[styles.fbChipText, feedbackType === t && { color: colors.white }]}>
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
            <TextInput
              style={styles.fbInput}
              placeholder="Tell us more..."
              placeholderTextColor={colors.textLight}
              value={feedbackMessage}
              onChangeText={setFeedbackMessage}
              multiline
              numberOfLines={3}
              maxLength={500}
            />
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.xs, marginTop: spacing.sm }}>
              <Text style={{ fontSize: fontSize.xs, color: colors.textSecondary }}>Rating:</Text>
              {[1, 2, 3, 4, 5].map(n => (
                <TouchableOpacity key={n} onPress={() => setFeedbackRating(n)}>
                  <Ionicons name={n <= feedbackRating ? 'star' : 'star-outline'} size={22} color={n <= feedbackRating ? '#f59e0b' : colors.textLight} />
                </TouchableOpacity>
              ))}
            </View>
            <View style={styles.btnRow}>
              <Button title="Cancel" onPress={() => { setShowFeedback(false); setFeedbackMessage(''); setFeedbackRating(0); }} variant="ghost" style={{ flex: 1 }} />
              <Button title="Send" onPress={handleSubmitFeedback} loading={sendingFeedback} style={{ flex: 1 }} />
            </View>
          </Card>
        )}

        {/* Delete Account */}
        {!showDeleteAccount ? (
          <Button
            title="Delete Account"
            onPress={() => setShowDeleteAccount(true)}
            variant="ghost"
            icon={<Ionicons name="trash-outline" size={20} color={colors.danger} />}
            style={[styles.actionBtn, { borderWidth: 1, borderColor: colors.danger + '40', borderRadius: borderRadius.md }]}
          />
        ) : (
          <Card style={[styles.editCard, { borderColor: colors.danger + '40', borderWidth: 1 }]}>
            <Text style={styles.sectionTitle}>Delete Account</Text>
            <Text style={{ fontSize: fontSize.sm, color: colors.danger, marginTop: -spacing.sm, marginBottom: spacing.sm, lineHeight: 20 }}>
              This will permanently delete your account and all associated data. This action cannot be undone.
            </Text>
            <Input label="Enter your password to confirm" icon="lock-closed-outline" value={deletePassword} onChangeText={setDeletePassword} secureTextEntry />
            {deleteError ? <Text style={styles.error}>{deleteError}</Text> : null}
            <View style={styles.btnRow}>
              <Button title="Cancel" onPress={() => { setShowDeleteAccount(false); setDeletePassword(''); setDeleteError(''); }} variant="ghost" style={{ flex: 1 }} />
              <Button title="Delete Account" onPress={handleDeleteAccount} loading={deletingAccount} variant="danger" style={{ flex: 1 }} />
            </View>
          </Card>
        )}

        <Button title="Sign Out" onPress={handleLogout} variant="danger" style={styles.logoutBtn} />
        {confirmLogout && (
          <InlineConfirm
            message="Are you sure you want to sign out?"
            visible={true}
            onConfirm={() => {
              setConfirmLogout(false);
              logout();
            }}
            onCancel={() => setConfirmLogout(false)}
            confirmLabel="Sign Out"
            variant="danger"
          />
        )}
        {/* Copyright — always the last thing on the screen */}
        <Text style={styles.copyright}>{'\u00A9'} 2026 RinsePay. All rights reserved.</Text>
        <View style={{ height: spacing.xxl }} />
        </ContentContainer>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  content: { padding: spacing.md },
  profileCard: { alignItems: 'center', marginBottom: spacing.md },
  avatar: {
    width: rs(64), height: rs(64), borderRadius: rs(32),
    backgroundColor: c.primaryBg,
    justifyContent: 'center', alignItems: 'center', marginBottom: spacing.md,
  },
  name: { fontSize: fontSize.xl, fontWeight: fontWeight.bold, color: c.text },
  email: { fontSize: fontSize.sm, color: c.textSecondary, marginTop: spacing.xs },
  phone: { fontSize: fontSize.sm, color: c.textLight, marginTop: 2 },
  accountRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.md, backgroundColor: c.primaryBg, paddingHorizontal: spacing.md, paddingVertical: spacing.sm, borderRadius: borderRadius.md, alignSelf: 'center' },
  accountLabel: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.textSecondary },
  accountNumber: { fontSize: fontSize.md, fontWeight: fontWeight.bold, color: c.primaryDark, fontFamily: 'monospace' },
  actionBtn: { marginBottom: spacing.md },
  editCard: { marginBottom: spacing.md },
  sectionTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text, marginBottom: spacing.md },
  sectionDesc: { fontSize: fontSize.sm, color: c.textSecondary, marginTop: -spacing.sm },
  btnRow: { flexDirection: 'row', gap: spacing.sm, marginTop: spacing.sm },
  section: { marginBottom: spacing.md },
  fbChip: { paddingHorizontal: rs(14), paddingVertical: rs(6), borderRadius: rs(16), backgroundColor: c.surfaceSecondary, borderWidth: 1, borderColor: c.border },
  fbChipActive: { backgroundColor: c.primary, borderColor: c.primary },
  fbChipText: { fontSize: fontSize.xs, color: c.textSecondary, fontWeight: fontWeight.medium },
  fbInput: { backgroundColor: c.surfaceSecondary, borderRadius: borderRadius.sm, padding: spacing.sm, fontSize: fontSize.sm, color: c.text, borderWidth: 1, borderColor: c.border, minHeight: 80, textAlignVertical: 'top' },
  error: { fontSize: fontSize.sm, color: c.danger, backgroundColor: c.danger + '10', padding: spacing.md, borderRadius: borderRadius.sm, marginBottom: spacing.sm, textAlign: 'center' },
  copyright: { fontSize: fontSize.xs, color: c.textLight, textAlign: 'center', marginBottom: spacing.md },
  logoutBtn: { marginTop: spacing.lg },
  selectWrapper: { marginTop: spacing.xs },
  tzOption: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: spacing.sm + 2, paddingHorizontal: spacing.sm, borderRadius: borderRadius.md, marginBottom: 2 },
  tzOptionSelected: { backgroundColor: c.primaryBg },
  tzLabel: { fontSize: fontSize.sm, color: c.text },
  tzLabelSelected: { color: c.primary, fontWeight: fontWeight.semibold },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
